import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download } from "lucide-react";

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export let triggerInstallPrompt = null;

export default function InstallPromptBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Don't show on desktop, standalone, or permanently dismissed
    if (!isMobileDevice() || isStandalone()) return;
    if (localStorage.getItem("install_prompt_dismissed") === "true") return;

    // Check temp dismiss (3-day cooldown)
    const tempDismissed = localStorage.getItem("install_prompt_dismissed_temp");
    if (tempDismissed && Date.now() - parseInt(tempDismissed) < THREE_DAYS_MS) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
      // Expose for manual trigger from profile
      triggerInstallPrompt = e;
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        localStorage.setItem("install_prompt_dismissed", "true");
        setVisible(false);
      }
    } catch {
      // prompt already used
    }
    setInstalling(false);
    setDeferredPrompt(null);
    triggerInstallPrompt = null;
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    localStorage.setItem("install_prompt_dismissed_temp", Date.now().toString());
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-[72px] left-1/2 -translate-x-1/2 w-[calc(100%-24px)] max-w-[calc(448px-24px)] z-[9990]"
          data-testid="install-prompt-banner"
        >
          <div className="bg-[#1C1C1E] border border-white/10 rounded-2xl p-3.5 shadow-2xl flex items-center gap-3">
            {/* App icon */}
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-lg">
              <span className="text-lg font-black text-white">BF</span>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-white leading-tight">Add to Home Screen</p>
              <p className="text-[11px] text-white/50 leading-tight mt-0.5">Get the best experience</p>
            </div>

            {/* Install button */}
            <button
              onClick={handleInstall}
              disabled={installing}
              className="flex items-center gap-1.5 px-4 py-2.5 min-h-[44px] rounded-xl bg-[#F59E0B] hover:bg-[#D97706] text-[#1C1C1E] text-xs font-bold transition-colors flex-shrink-0 disabled:opacity-50"
              data-testid="install-prompt-install-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Install
            </button>

            {/* Dismiss */}
            <button
              onClick={handleDismiss}
              className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#2C2C2E] border border-white/10 flex items-center justify-center text-white/40 hover:text-white/80 transition"
              data-testid="install-prompt-dismiss-btn"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
